import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { MenuDTO } from "src/app/models/Menu/MenuDTO";
import { ProductExtendedDTO } from "src/app/models/Products/productDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import {
  getCategoriesForListByUsage,
  getPropertiesForListByUsage,
} from "src/app/store/actions/parties/parties.actions";
import {
  getProductsTableConfig,
  saveProductsTableConfig,
} from "src/app/store/actions/products/product.actions";
import {
  AppState,
  loadedCategoriesListPartyState,
  loadedProductExcelListState,
  loadedProductIsLoadingState,
  loadedProductListState,
  loadedProductTotalState,
  loadedPropertiesListPartyState,
} from "src/app/store/reducers";
import { getColorByBgColor } from "../../utils/colorUtils";
import { setCategories, setProperties } from "../../utils/tableUtils";

const USAGE_CODE = "PRODUCT";

export class BaseProductsList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  productsList$: Observable<ProductExtendedDTO[]>;
  excelProductsList$: Observable<ProductExtendedDTO[]>;
  totalProducts$: Observable<number>;

  productSubs: Subscription;
  productList: ProductExtendedDTO[];

  categories$: Observable<CategoryDTO[]>;
  categories: CategoryDTO[];

  properties$: Observable<PropertyDTO[]>;
  properties: PropertyDTO[];

  multiSubs: Subscription;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "products/product";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Products.code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
      searchable: true,
    },
    {
      header: "Products.description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Products.shortDescription",
      field: "shortDescription",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Products.productGroupCode",
      field: "group.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productGroupDescription",
      field: "group.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.productFamilyCode",
      field: "family.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productFamilyDescription",
      field: "family.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.tax",
      field: "taxDescription",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.imageUrl",
      field: "imageUrl",
      fieldType: FieldType.IMAGE,
    },
    {
      header: "Products.referenceUnitPrice",
      field: "referenceUnitPrice",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      fuelFormatType: FormatType.FUELUNITPRICE,
    },
    {
      header: "Products.color",
      field: "color",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Products.productUnitDescription",
      field: "unit.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.defaultQuantity",
      field: "defaultQuantity",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Products.refundMaxPeriod",
      field: "refundMaxPeriod",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Products.productUnitEnabled",
      field: "unit.enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.productGroupEnabled",
      field: "group.enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.isFuel",
      field: "isFuel",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.promptQuantity",
      field: "promptQuantity",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.customPrice",
      field: "customPrice",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.promptPrice",
      field: "promptPrice",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.priceByWeight",
      field: "priceByWeight",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.fiscalInfoRequired",
      field: "fiscalInfoRequired",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.refundable",
      field: "refundable",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.canSellBelowBuyPrice",
      field: "canSellBelowBuyPrice",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.packs",
      field: "pack",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Products.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    public store: Store<AppState>,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(loadedProductIsLoadingState));
    this.productsList$ = this.store.pipe(select(loadedProductListState));
    this.excelProductsList$ = this.store.pipe(select(loadedProductExcelListState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.totalProducts$ = this.store.pipe(select(loadedProductTotalState));
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Products")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  setTableCustomElements() {
    this.productSubs = this.productsList$
      .pipe(skip(1))
      .subscribe((products: any[]) => {
        this.productList = products;

        if (this.productList && this.productList?.length) {
          this.productList = this.productList.map((product) => {
            let treatedProduct = product;
            treatedProduct = this.setCustomProductChip(treatedProduct);
            return treatedProduct;
          });
        }
      });
  }

  setCustomProductChip(product: any) {
    const defaultProduct = {
      ...product,
    };

    const { color } = product;

    if (color === undefined) {
      return defaultProduct;
    }

    if (color !== undefined && color !== null) {
      let bgColor = getColorByBgColor(`#${color}`);
      let productColor = {
        ...product,
        color: {
          value: color,
          icon: "format_color_fill",
          style: `background-color: #${color}; color: ${bgColor};`,
        },
      };
      return productColor;
    }
  }

  getProductCategoriesProperties(usageCode: string, getProducts: Function) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }
      this.getTableConfig(usageCode);
      this.setTableCustomElements();
      getProducts();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getProductsTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getProducts: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getProducts(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getProductsExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getProductsExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveProductsTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
